import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Computer,
  PhoneAndroid,
  Tablet,
  Logout,
  Refresh,
  Security,
  LocationOn,
  AccessTime,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const SessionManagement = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutAllDialogOpen, setLogoutAllDialogOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [processing, setProcessing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/auth/sessions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSessions(response.data.sessions || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch sessions');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutSession = async (sessionId) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/auth/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess('Session logged out successfully');
      setLogoutDialogOpen(false);
      setSelectedSessionId(null);
      fetchSessions();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to logout session');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessing(false);
    }
  };

  const handleLogoutAll = async () => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/auth/sessions/logout-all`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccess('Logged out from all other devices successfully');
      setLogoutAllDialogOpen(false);
      fetchSessions();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to logout from all sessions');
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessing(false);
    }
  };

  const getDeviceIcon = (device) => {
    const deviceLower = device.toLowerCase();
    if (deviceLower.includes('android') || deviceLower.includes('iphone') || deviceLower.includes('mobile')) {
      return <PhoneAndroid />;
    }
    if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) {
      return <Tablet />;
    }
    return <Computer />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expiry - now) / (1000 * 60 * 60);
    return hoursUntilExpiry < 2 && hoursUntilExpiry > 0;
  };

  const activeSessions = sessions; // All sessions returned are active
  const currentSession = sessions.find(s => s.isCurrent);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Session Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSessions}
            disabled={loading}
          >
            Refresh
          </Button>
          {activeSessions.length > 1 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Logout />}
              onClick={() => setLogoutAllDialogOpen(true)}
            >
              Logout All Other Devices
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Active Sessions
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {activeSessions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Sessions
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {sessions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Device
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" fontSize="small" />
                {currentSession?.device || 'This Device'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Security Notice */}
      <Alert severity="info" icon={<Security />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Security Tip:</strong> Regularly review your active sessions. If you notice any suspicious activity, 
          immediately logout from that session and change your password.
        </Typography>
      </Alert>

      {/* Sessions Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
              No sessions found
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Login Time</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow
                  key={session.id}
                  sx={{
                    backgroundColor: session.isCurrent ? 'action.selected' : 'inherit',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getDeviceIcon(session.device)}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {session.device}
                        </Typography>
                        {session.isCurrent && (
                          <Chip
                            label="Current Session"
                            color="success"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{session.location}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {session.ipAddress}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{formatDate(session.loginTime)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(session.loginTime)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{formatDate(session.lastActivity)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(session.lastActivity)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {session.isCurrent ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="Active"
                        color="success"
                        size="small"
                      />
                    ) : isExpiringSoon(session.expiresAt) ? (
                      <Chip
                        icon={<Warning />}
                        label="Expiring Soon"
                        color="warning"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Active"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {!session.isCurrent && (
                      <Tooltip title="Logout from this device">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedSessionId(session.id);
                            setLogoutDialogOpen(true);
                          }}
                        >
                          <Logout />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Logout Single Session Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => !processing && setLogoutDialogOpen(false)}>
        <DialogTitle>Logout from Device?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout from this device? You will need to login again to access from that device.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={() => handleLogoutSession(selectedSessionId)}
            color="error"
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : <Logout />}
          >
            {processing ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout All Sessions Dialog */}
      <Dialog open={logoutAllDialogOpen} onClose={() => !processing && setLogoutAllDialogOpen(false)}>
        <DialogTitle>Logout from All Other Devices?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will logout your account from all other devices except this one. 
            You will remain logged in on this device. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutAllDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleLogoutAll}
            color="error"
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : <Logout />}
          >
            {processing ? 'Logging out...' : 'Logout All Others'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionManagement;

